import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n';
import { bulkImportService } from '@/services/bulkImport.service';
import {
  BulkImportJobStatus,
  BulkImportJob,
  BulkImportOptions,
} from '@/types/bulkImport';
import { toast } from '@/utils/toast';
import { useBulkImportStore } from '@/stores/bulkImportStore';
import { formatApiError } from '@/services/api';

/**
 * React Query hooks for bulk import
 */

/**
 * Start bulk import mutation
 */
export const useStartBulkImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      options,
      importType = 'posts'
    }: {
      file: File;
      options?: BulkImportOptions;
      importType?: 'posts' | 'variations';
    }) =>
      bulkImportService.startImport(file, options, importType),
    onSuccess: (data) => {
      toast.success(`Import started: ${data.message}`);
      queryClient.invalidateQueries({ queryKey: ['bulk-import-jobs'] });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Get job status - SSE-first with polling fallback.
 *
 * Progress comes from SSE events pushed by the worker. React Query polls at
 * 2-second intervals as a fallback for missed or dropped SSE events (e.g. the
 * browser missed `bulk:import:completed`). Polling stops automatically once the
 * job reaches a terminal state (COMPLETED, FAILED, CANCELLED).
 */
export const useBulkImportStatus = (jobId: number | null) => {
  const activeImport = useBulkImportStore((s) => s.activeImport);

  const TERMINAL_STATUSES = new Set<BulkImportJobStatus['status']>([
    'COMPLETED',
    'FAILED',
    'CANCELLED',
  ]);

  const query = useQuery<BulkImportJobStatus, Error>({
    queryKey: ['bulk-import-status', jobId],
    queryFn: () => bulkImportService.getJobStatus(jobId!),
    enabled: !!jobId,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      return TERMINAL_STATUSES.has(data.status) ? false : 2000;
    },
    refetchIntervalInBackground: true,
  });

  // Merge SSE progress into query data if available for this job
  if (activeImport?.status && activeImport.jobId === jobId && query.data) {
    return {
      ...query,
      data: {
        ...query.data,
        status: activeImport.status.status,
        processedRows: activeImport.status.processedRows,
        totalRows: activeImport.status.totalRows,
        successCount: activeImport.status.successCount,
        failureCount: activeImport.status.failureCount,
        percentComplete: activeImport.status.percentComplete,
      },
    };
  }

  return query;
};

/**
 * Cancel a running import job
 */
export const useCancelBulkImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: number) => bulkImportService.cancelJob(jobId),
    onSuccess: (_, jobId) => {
      toast.success(i18n.t('bulk_import_cancelled'));
      queryClient.invalidateQueries({ queryKey: ['bulk-import-status', jobId] });
      queryClient.invalidateQueries({ queryKey: ['bulk-import-jobs'] });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Get user's import job history
 */
export const useBulkImportJobs = () => {
  return useQuery<BulkImportJob[], Error>({
    queryKey: ['bulk-import-jobs'],
    queryFn: bulkImportService.getJobs,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Export variations template for selected posts
 */
export const useExportVariationsTemplate = () => {
  return useMutation({
    mutationFn: (postIds: number[]) =>
      bulkImportService.exportVariationsTemplate(postIds),
    onSuccess: () => {
      toast.success(i18n.t('bulk_import_template_success'));
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Helper function to download template
 * Not a hook - just triggers download
 */
export const downloadTemplate = async (
  format: 'csv' | 'xlsx',
  importType: 'posts' | 'variations' = 'posts'
): Promise<void> => {
  try {
    const blob = await bulkImportService.downloadTemplate(format, importType);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const prefix = importType === 'variations' ? 'variation-import' : 'bulk-import';
    a.download = `${prefix}-template.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Template downloaded: ${format.toUpperCase()}`);
  } catch (error) {
    toast.error(formatApiError(error).message);
  }
};
